/**
 * Date formatting for kickoff times.
 *
 * Always rendered in the viewer's own zone: a pool is shared by people who may
 * not all be in the stadium's timezone, and "3:30 PM" meaning *their* 3:30 is
 * the only reading that is never wrong for them.
 */

/** "Sat, Sep 12 · 3:30 PM". Empty string when there is no usable date. */
export function formatKickoff(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
    .format(date)
    .replace(/,\s*(?=\d?\d:)/, ' · ');
}

/** "Sat, Sep 12" -- for lists, where the time is noise. */
export function formatKickoffDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * A cutoff, with the timezone spelled out.
 *
 * The admin sets this in their own local time, but players read it from
 * wherever they are. Rendering it in the reader's zone is necessary but not
 * sufficient -- "closes 3:00 PM" is ambiguous enough to cost someone their
 * squares, so the zone is always named.
 */
export function formatDeadline(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
}

/**
 * Dollars, with cents only when the amount has them.
 *
 * Both formatters used to be pinned to whole dollars, so a penny-a-square
 * board reported a pot of "$1" and payouts of "$0" -- indistinguishable from
 * the price change having been ignored.
 */
const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatMoney(amount: number): string {
  return MONEY.format(Number.isFinite(amount) ? amount : 0);
}
